/**
 * Every URL the app owns, as constants.
 *
 * A URL is part of the product: it is what a facilitator pastes into a meeting
 * invite and what an executive bookmarks. String literals at call sites drift
 * from the route definitions, and the failure mode — a link that 404s — is
 * invisible until someone clicks it.
 *
 * Builders take ids rather than interpolating at the call site, so there is one
 * place that knows a method version's URL contains its id and nothing else.
 */

export const PATHS = {
  root: '/',

  /* Unauthenticated. Deliberately outside the shell: the black chrome implies a
     signed-in workspace, and showing it around a login form is a lie. */
  login: '/login',
  register: '/register',

  explorer: '/explorer',
  authoring: '/authoring',
  compare: '/compare',
  workshop: '/workshop',
  plans: '/plans',
  admin: '/admin',

  /* Catalogue and governance are sections of the authoring surface rather than
     surfaces of their own (architecture §7 gives authoring `catalog:manage` and
     the review queue). They are top-level nav entries because that is how the
     mockups present them and how practitioners think of them. */
  catalogue: '/authoring/catalogue',
  governance: '/authoring/governance',

  forbidden: '/forbidden',
} as const;

export const routeTo = {
  /** A method version in the authoring canvas. */
  method: (methodVersionId: string): string => `${PATHS.authoring}/${methodVersionId}`,
  /** One element selected within a method version. */
  methodElement: (methodVersionId: string, elementId: string): string =>
    `${PATHS.authoring}/${methodVersionId}/elements/${elementId}`,

  /** A published method in the explorer. */
  published: (publishedMethodId: string): string => `${PATHS.explorer}/${publishedMethodId}`,
  publishedElement: (publishedMethodId: string, elementId: string): string =>
    `${PATHS.explorer}/${publishedMethodId}/elements/${elementId}`,

  /**
   * Standard against agentic. Both ids are in the path rather than the query
   * because the pair *is* the resource — it is what gets shared and cited.
   */
  compare: (baselineId: string, candidateId: string): string =>
    `${PATHS.compare}/${baselineId}/${candidateId}`,

  workshopSession: (sessionId: string): string => `${PATHS.workshop}/${sessionId}`,

  /**
   * One plan's work breakdown. The plan id alone: a plan is generated from a
   * method version and a template, but it is a resource in its own right from
   * that moment on — its tasks get owners, durations and dates that neither
   * input knows about, so neither belongs in its URL.
   */
  plan: (planId: string): string => `${PATHS.plans}/${planId}`,

  catalogueKind: (kind: string): string => `${PATHS.catalogue}/${kind}`,

  adminSection: (section: string): string => `${PATHS.admin}/${section}`,
} as const;

/** Where a signed-in user lands with no destination of their own. */
export const DEFAULT_AUTHENTICATED_PATH: string = PATHS.explorer;

/**
 * The query parameter that carries "where they were going" through the login
 * round trip. Read back by the login screen, never trusted as an external URL:
 * `safeRedirectTarget` below is what makes it safe.
 */
export const REDIRECT_PARAM = 'next';

/**
 * An open redirect is trivially exploitable as a phishing hop — a link to *our*
 * login page that lands on someone else's site carries our domain's credibility.
 * Only a same-site, absolute, single-slash path is honoured; anything else falls
 * back to the default surface.
 */
export const safeRedirectTarget = (raw: string | null | undefined): string => {
  if (raw === null || raw === undefined) return DEFAULT_AUTHENTICATED_PATH;
  // `//evil.example` is protocol-relative and leaves the origin, and a
  // backslash is normalised to a slash by some parsers but not others.
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) {
    return DEFAULT_AUTHENTICATED_PATH;
  }
  if (raw === PATHS.login || raw === PATHS.register) return DEFAULT_AUTHENTICATED_PATH;
  return raw;
};
